/* terminal/sagas*/
import { isEmpty } from "ramda";
import { all, fork, put, spawn, takeEvery, takeLatest } from "redux-saga/effects";
import { isAuthError } from "../";
import { drop, fetch, save, upload } from "../../repo/terminal";
import { dirty } from "../session/actions";
import {
  deleteError,
  deleteRequest,
  deleteSuccess,
  fetchError,
  fetchRequest,
  fetchSuccess,
  saveBulk,
  saveError,
  saveRequest,
  saveSuccess,
  uploadFile,
  uploadFileError,
  uploadFileSuccess,
} from "./actions";
import { Terminal, TerminalActionTypes } from "./types";
import { exportToVSC, flat } from "../../lib/utils";
import { get } from "lodash";

const delay = (ms: number) =>
  new Promise((response) => setTimeout(response, ms));

function* handleFetch(action: ReturnType<typeof fetchRequest>) {
  try {
    const terminal: Terminal = yield fetch(
      action.payload.id,
      action.payload.params
    );
    if (terminal.errors && !isEmpty(terminal.errors)) {
      if (isAuthError(terminal.errors)) {
        yield put(dirty());
      }
      yield put(fetchError(JSON.stringify(terminal.errors)));
      action.payload.callback &&
        action.payload.callback(JSON.stringify(terminal.errors), null);
    } else {
      yield put(fetchSuccess(terminal));
      yield delay(100); //Seems the callback had a timing issue with the state. The delay helped but likely not the best fix.
      action.payload.callback && action.payload.callback(null, terminal);
    }
  } catch (err) {
    yield put(fetchError(err));
    action.payload.callback &&
      action.payload.callback(
        "An error occurred while fetching Terminals",
        null
      );
  }
}

function* watchFetchRequest() {
  yield takeEvery(TerminalActionTypes.FETCH, handleFetch);
}

function* handleSave(action: ReturnType<typeof saveRequest>) {
  try {
    // If these Feature* Enum values are not set, we need them defaulted to match the backend mongo schema.
    if (!action.payload.data.attributes.FeatureCashOut)
      action.payload.data.attributes.FeatureCashOut = "Yes";
    if (!action.payload.data.attributes.FeaturePurWithCash)
      action.payload.data.attributes.FeaturePurWithCash = "Yes";
    if (!action.payload.data.attributes.FeaturePurWithTip)
      action.payload.data.attributes.FeaturePurWithTip = "No";
    if (!action.payload.data.attributes.FeatureRefund)
      action.payload.data.attributes.FeatureRefund = "Yes";
    if (!action.payload.data.attributes.FeaturePreAuth)
      action.payload.data.attributes.FeaturePreAuth = "No";
    if (!action.payload.data.attributes.FeatureCompletion)
      action.payload.data.attributes.FeatureCompletion = "No";
    if (!action.payload.data.attributes.FeatureManualSettlement)
      action.payload.data.attributes.FeatureManualSettlement = "Yes";
    if (!action.payload.data.attributes.Standalone)
      action.payload.data.attributes.Standalone = "Yes";
    if (!action.payload.data.attributes.CloudPOS)
      action.payload.data.attributes.CloudPOS = "Yes";
    if (!action.payload.data.attributes.POSApp)
      action.payload.data.attributes.POSApp = "Yes";
    if (!action.payload.data.attributes.BpCardSupport)
      action.payload.data.attributes.BpCardSupport = "No";
    if (!action.payload.data.attributes.AllowBasketData)
      action.payload.data.attributes.AllowBasketData = "No";

    const terminal: Terminal = yield save(action.payload.data);
    // we may still have an error
    // if so then we should yield error
    if (terminal.errors && !isEmpty(terminal.errors)) {
      if (isAuthError(terminal.errors)) {
        yield put(dirty());
      }
      yield put(saveError(JSON.stringify(terminal.errors)));
      action.payload.callback &&
        action.payload.callback(JSON.stringify(terminal.errors), null);
    } else {
      yield put(saveSuccess(terminal));
      action.payload.callback && action.payload.callback(null, terminal);
    }
  } catch (err) {
    yield put(saveError(err));
    action.payload.callback &&
      action.payload.callback("An error occurred while saving Terminal", null);
  }
}

function* watchSaveRequest() {
  yield takeEvery(TerminalActionTypes.SAVE_REQUEST, handleSave);
}

function* handleSaveBulk(action: ReturnType<typeof saveBulk>) {
  try {
    // If these Feature* Enum values are not set, we need them defaulted to match the backend mongo schema.
    if (!action.payload.data.attributes.FeatureCashOut)
      action.payload.data.attributes.FeatureCashOut = "Yes";
    if (!action.payload.data.attributes.FeaturePurWithCash)
      action.payload.data.attributes.FeaturePurWithCash = "Yes";
    if (!action.payload.data.attributes.FeaturePurWithTip)
      action.payload.data.attributes.FeaturePurWithTip = "No";
    if (!action.payload.data.attributes.FeatureRefund)
      action.payload.data.attributes.FeatureRefund = "Yes";
    if (!action.payload.data.attributes.FeaturePreAuth)
      action.payload.data.attributes.FeaturePreAuth = "No";
    if (!action.payload.data.attributes.FeatureCompletion)
      action.payload.data.attributes.FeatureCompletion = "No";
    if (!action.payload.data.attributes.FeatureManualSettlement)
      action.payload.data.attributes.FeatureManualSettlement = "Yes";
    if (!action.payload.data.attributes.Standalone)
      action.payload.data.attributes.Standalone = "Yes";
    if (!action.payload.data.attributes.CloudPOS)
      action.payload.data.attributes.CloudPOS = "Yes";
    if (!action.payload.data.attributes.POSApp)
      action.payload.data.attributes.POSApp = "Yes";
    if (!action.payload.data.attributes.BpCardSupport)
      action.payload.data.attributes.BpCardSupport = "No";
    if (!action.payload.data.attributes.AllowBasketData)
      action.payload.data.attributes.AllowBasketData = "No";

    const terminal: Terminal = yield save(action.payload.data);
    console.log({
      terminalSave: terminal.errors
    })
    if (terminal.errors && !isEmpty(terminal.errors)) {
      if (isAuthError(terminal.errors)) {
        yield put(dirty());
      }
      yield put(saveError(JSON.stringify(terminal.errors)));
      action.payload.callback &&
        action.payload.callback(JSON.stringify(terminal.errors), null);
    } else {
      yield put(saveSuccess(terminal));
      action.payload.callback && action.payload.callback(null, terminal);
    }
  } catch (err) {
    yield put(saveError(err));
    action.payload.callback &&
      action.payload.callback(
        "An error occurred while saving group of Terminals",
        null
      );
  }
}

function* watchSaveBulk() {
  yield takeEvery(TerminalActionTypes.SAVE_BULK, handleSaveBulk);
}

function* handleDelete(action: ReturnType<typeof deleteRequest>) {
  try {
    const dropResponse = yield drop(action.payload.id);

    if (dropResponse.errors && !isEmpty(dropResponse.errors)) {
      if (isAuthError(dropResponse.errors)) {
        yield put(dirty());
      }
      yield put(deleteError(JSON.stringify(dropResponse.errors)));
      action.payload.callback &&
        action.payload.callback(JSON.stringify(dropResponse.errors));
    } else {
      yield put(deleteSuccess());
      action.payload.callback && action.payload.callback(null);
      yield put(fetchRequest(null));
    }
  } catch (err) {
    yield put(deleteError(err));
    action.payload.callback && action.payload.callback(JSON.stringify(err));
  }
}

function* watchDeleteRequest() {
  yield takeLatest(TerminalActionTypes.DELETE_REQUEST, handleDelete);
}

function* handleUploadFile(action: ReturnType<typeof uploadFile>) {
  try {
    const uploadResponse = yield upload(action.payload.data);
    if (uploadResponse.errors && uploadResponse.errors.length > 0) {
      if (isAuthError(uploadResponse.errors)) {
        yield put(dirty());
      }
      yield put(uploadFileError(JSON.stringify(uploadResponse.errors)));
      action.payload.callback &&
        action.payload.callback(JSON.stringify(uploadResponse.errors), null);
    } else {
      yield put(uploadFileSuccess(uploadResponse));
      action.payload.callback && action.payload.callback(null, uploadResponse);
    }
  } catch (err) {
    yield put(uploadFileError(err));
    action.payload.callback &&
      action.payload.callback(
        "An error occurred while importing KIF Terminal data",
        null
      );
  }
}

function* handleDownloadRequest(action: ReturnType<typeof fetchRequest>) {
  try {
    const response = yield fetch(action.payload.id, action.payload.params);
    if (response.errors && !isEmpty(response.errors)) {
      if (isAuthError(response.errors)) {
        yield put(dirty());
      }
      yield put(fetchError(JSON.stringify(response.errors)));
    } else {
      // export to csv file
      const header = action.payload.params["header"] ?? [];
      const excludeFields = action.payload.params["excludeFields"] ?? [];
      exportToVSC(response.data, header, excludeFields);
      // const rows = [
      //   header
      // ]
      // data.data.forEach((merchant:any) => {
      //   Object.keys(flat(merchant.attributes,'root')).forEach(key=>{
      //     const column =  key.replace("root.","");
      //     if(!header.includes(column)) {
      //       header.push(column);
      //     }
      //   });
      //   rows.push(header.map(column=>{
      //     return get(merchant.attributes, column);
      //   }))
      // })
      // let csvContent = "data:text/csv;charset=utf-8,"
      //   + rows.map(e => e.join(",")).join("\n");
      // const encodedUri = encodeURI(csvContent);
      // window.open(encodedUri);
    }
  } catch (err) {
    yield put(fetchError(err));
  }
}

function* watchUploadFileRequest() {
  yield takeEvery(TerminalActionTypes.UPLOAD_FILE, handleUploadFile);
}

function* watchDownloadRequest() {
  yield takeEvery(TerminalActionTypes.DOWNLOAD_REQUEST, handleDownloadRequest);
}

export default function* terminalSaga() {
  yield all([
    spawn(watchFetchRequest),
    spawn(watchSaveRequest),
    spawn(watchSaveBulk),
    spawn(watchDeleteRequest),
    spawn(watchUploadFileRequest),
    spawn(watchDownloadRequest),
  ]);
}
